import React, {FC, PropsWithChildren, useEffect, useRef} from 'react';
import {observer} from 'mobx-react-lite';
import merge from 'lodash/merge';
import {useService} from '@yosmanyga/di';
import {ThemeBuildType, ThemeProvider, ThemeVariablesType} from '@yosmanyga/theme';
import {Button, Container, I18NText, Icon, Input, useIcons,} from '@yosmanyga/rich-ui';
import {PlayInputController} from '../../controller/';

const PlayInput = observer(({
    templates,
    onInput,
    onNavigateToRestore,
    onNavigateToHelp
}: PlayInputProps) => {
    const icons = useIcons();

    const controller = useService(PlayInputController, []);

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        ref.current.focus();
    }, [controller.focus])

    if (controller.dead) {
        return <templates.layout>
            <Button onClick={onNavigateToRestore}>
                <Icon
                    component={icons.objects.restoration}
                    size='xs'
                />
                <I18NText>Move back</I18NText>
            </Button>
        </templates.layout>
    }

    if (controller.won) {
        return <templates.layout/>
    }

    return <templates.layout>
        <Container
            flow="row"
            align={{
                cross: 'center',
            }}
            margin={{
                top: 2,
            }}
        >
            <I18NText
                flex={{
                    grow: 1,
                    shrink: 1
                }}
                intent="system"
            >
                What do you want to do?
            </I18NText>
            <Container
                flow="row"
                align={{
                    main: 'flex-end',
                    cross: 'center',
                }}
            >
                <Button
                    margin={{
                        left: 1,
                    }}
                    onClick={() => {
                        controller.reuse(-1);
                    }}
                >
                    <Icon
                        component={icons.actions.up}
                        size='xs'
                    />
                </Button>
                <Button
                    onClick={() => {
                        controller.reuse(1);
                    }}
                >
                    <Icon
                        component={icons.actions.down}
                        size='xs'
                    />
                </Button>
                <Button
                    onClick={onNavigateToHelp}
                >
                    <Icon
                        component={icons.objects.help}
                        size='xs'
                    />
                </Button>
            </Container>
        </Container>

        <Container
            flow="row"
            align={{
                cross: 'center',
            }}
        >
            <Input
                ref={ref}
                flex={{
                    grow: 1,
                    shrink: 1,
                }}
                value={controller.input}
                onChange={(value) => {
                    controller.prepare(value);
                }}
                onEnter={async () => {
                    if (!controller.input) {
                        return;
                    }

                    await controller.execute();

                    onInput(controller.input);
                }}
                onKeyDown={(key) => {
                    if (key === 'ArrowUp') {
                        controller.reuse(-1);
                    } else if (key === 'ArrowDown') {
                        controller.reuse(1);
                    }
                }}
                focus
                capitalize="sentences"
            />
            <Button
                id='submit'
                margin={{
                    left: 1,
                }}
                disabled={!controller.input}
                progress={controller.executing}
                onClick={async () => {
                    await controller.execute();

                    onInput(controller.input);
                }}
            >
                <Icon
                    component={icons.actions.play}
                    size='xs'
                />
            </Button>
        </Container>
    </templates.layout>
});

type PlayInputProps = {
    templates: {
        layout: FC<PropsWithChildren>;
    };
    onInput: (input: string) => void;
    onNavigateToRestore: () => void;
    onNavigateToHelp: () => void;
};

const PlayInputWithTheme = (props: PlayInputProps) => {
    return <ThemeProvider
        build={(variables?: ThemeVariablesType, parent?: ThemeBuildType) => {
            const {palette} = variables as ThemeVariablesType;

            const current = {
                text: {
                    system_intent: {
                        color: palette.neutral['1000'],
                    },
                },
                button: {
                    border: 0,
                    pseudo: {
                        hover: {
                            border: 0
                        }
                    },
                    is_disabled: {
                        pseudo: {
                            hover: {
                                border: 0
                            }
                        }
                    }
                },
                input_entry: {
                    color: palette.neutral['0'],
                },
            };

            return merge(
                {},
                parent ? parent(variables) : {},
                current,
            );
        }}
        variables={undefined}
    >
        <PlayInput {...props} />
    </ThemeProvider>
};

export {
    PlayInputWithTheme as PlayInput
};
